
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from '@/context/CartContext';
import { AuthProvider } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminLayout } from '@/layouts/AdminLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { ScrollToTop } from '@/utils/ScrollToTop';
import { isInAppBrowser } from '@/utils/webviewDetect';
import { useEffect, lazy, Suspense } from 'react';

// Eagerly loaded pages (critical path)
import Index from '@/pages/Index';
import ProductDetail from '@/pages/ProductDetail';
import NotFound from '@/pages/NotFound';
import PaymentCallback from '@/pages/PaymentCallback';

// Lazy-loaded public pages
const Shop = lazy(() => import('@/pages/Shop'));
const Cart = lazy(() => import('@/pages/Cart'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const Auth = lazy(() => import('@/pages/Auth'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Faq = lazy(() => import('@/pages/Faq'));
const PlantCare = lazy(() => import('@/pages/PlantCare'));
const PlantCareDetail = lazy(() => import('@/pages/PlantCareDetail'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Shipping = lazy(() => import('@/pages/Shipping'));
const Guarantees = lazy(() => import('@/pages/Guarantees'));
const Profile = lazy(() => import('@/pages/Profile'));
const Orders = lazy(() => import('@/pages/Orders'));
const OrderSuccess = lazy(() => import('@/pages/OrderSuccess'));
const Sustainability = lazy(() => import('@/pages/Sustainability'));
const Careers = lazy(() => import('@/pages/Careers'));
const AccessibilityStatement = lazy(() => import('@/pages/AccessibilityStatement'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
const Track = lazy(() => import('@/pages/Track'));

// Lazy-loaded admin pages
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Products = lazy(() => import('@/pages/admin/Products'));
const AdminOrders = lazy(() => import('@/pages/admin/Orders'));
const PromoCodes = lazy(() => import('@/pages/admin/PromoCodes'));
const Users = lazy(() => import('@/pages/admin/Users'));
const Testimonials = lazy(() => import('@/pages/admin/Testimonials'));
const PlantCareAdmin = lazy(() => import('@/pages/admin/PlantCare'));
const Categories = lazy(() => import('@/pages/admin/Categories'));
const Homepage = lazy(() => import('@/pages/admin/Homepage'));
const StaticPages = lazy(() => import('@/pages/admin/StaticPages'));
const AbandonedCarts = lazy(() => import('@/pages/admin/AbandonedCarts'));
const Analytics = lazy(() => import('@/pages/admin/Analytics'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: isInAppBrowser ? 3 : 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Prevent React Query from pausing queries when WebView misreports navigator.onLine
      networkMode: 'always',
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
});

function OnlineRecovery() {
  useEffect(() => {
    const handler = () => {
      queryClient.refetchQueries({ type: 'active' });
    };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, []);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <OnlineRecovery />
              <Suspense fallback={null}>
                <Routes>
                  {/* Payment callback - outside layout for instant rendering */}
                  <Route path="/payment-callback" element={<PaymentCallback />} />
                  
                  {/* Public routes */}
                  <Route path="/" element={<PublicLayout />}>
                    <Route index element={<Index />} />
                    <Route path="shop" element={<Shop />} />
                    <Route path="product/:id" element={<ProductDetail />} />
                    <Route path="cart" element={<Cart />} />
                    <Route path="checkout" element={<Checkout />} />
                    <Route path="auth" element={<Auth />} />
                    <Route path="reset-password" element={<ResetPassword />} />
                    <Route path="about" element={<About />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="faq" element={<Faq />} />
                    <Route path="plant-care" element={<PlantCare />} />
                    <Route path="plant-care/:id" element={<PlantCareDetail />} />
                    <Route path="privacy" element={<Privacy />} />
                    <Route path="terms" element={<Terms />} />
                    <Route path="shipping" element={<Shipping />} />
                    <Route path="guarantees" element={<Guarantees />} />
                    <Route path="sustainability" element={<Sustainability />} />
                    <Route path="careers" element={<Careers />} />
                    <Route path="accessibility" element={<AccessibilityStatement />} />
                    <Route path="cookie-policy" element={<CookiePolicy />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="change-password" element={<ChangePassword />} />
                    <Route path="orders" element={<Orders />} />
                    <Route path="order-success" element={<OrderSuccess />} />
                    <Route path="track/:orderId" element={<Track />} />
                  </Route>

                  {/* Admin routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Dashboard />} />
                    <Route path="products" element={<Products />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="abandoned-carts" element={<AbandonedCarts />} />
                    <Route path="promo-codes" element={<PromoCodes />} />
                    <Route path="users" element={<Users />} />
                    <Route path="testimonials" element={<Testimonials />} />
                    <Route path="plant-care" element={<PlantCareAdmin />} />
                    <Route path="categories" element={<Categories />} />
                    <Route path="homepage" element={<Homepage />} />
                    <Route path="static-pages" element={<StaticPages />} />
                    <Route path="analytics" element={<Analytics />} />
                  </Route>

                  {/* 404 page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <Toaster />
            </Router>
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
